import cn from 'classnames';
import { includes } from 'ramda';
import { useTranslation } from 'react-i18next';

import Render from 'components/render';
import { RenderForGlobalExchange } from 'components/render/RenderByExchangeType';
import {
  COUNTRIES_WITH_SINGLE_VERIFICATION_FLOW,
  IS_INDIAN_EXCHANGE,
} from 'constants/constants';
import { isUserPhoneVerifiedBeforeLevelOneInvalidation } from 'helpers/utils';
import { useRekycVerificationNavigation } from 'hooks/componentHooks/useRekycVerificationNavigation';
import i18n from 'i18n/config';
import { userSelector } from 'selectors/otherSelectors';
import {
  isUserLevelOneVerifiedSelector,
  isUserLevelThreeVerifiedSelector,
  isUserLevelTwoVerifiedSelector,
  onlyLevel2VerifiedSelector,
} from 'selectors/user';
import { useAppSelector } from 'storeHooks';
import { RedWarningIcon, TickVerified, TickYellow } from 'styles/assets/icons';
import DeltaButton from 'UIKit/DeltaButton';

import { UnverifiedIcon } from '../profileMenu/icons';
import styles from './kycStatusButton.module.scss';

const textKycRefreshRequired = IS_INDIAN_EXCHANGE
  ? i18n.t('trading:renewKyc')
  : i18n.t('account:profile.kycRefreshRequired');

/**
 * KYC Statuses & their respective status messages to be shown:
 * (Please Update Statuses & messages here as you change them)
 *
 * Single Verification Flow:
 * 1. Unverified --> "get verified"
 * 3. Verified - "Verified"
 *
 * Multi-Level Verification Flow:
 * 1. Unverified --> "get verified"
 * 2. Level one (Basic) --> Phone Verified Before 6th Jan 2023: "KYC Upgrade Required", Phone Verified After 6th Jan 2023: "get verified"
 * 3. Level two (Verified) --> "Verified"
 * 4. Level three (Verified Plus) --> "Verified Plus"
 *
 * Common KYC Statuses:
 * 1. Marked for kyc refresh --> "KYC Refresh Required"
 * 2. Provisional KYC --> "Prov. Verified"
 */

const KycStatusButton = ({ buttonClassName }: { buttonClassName?: string }) => {
  const { t } = useTranslation(['headerFooter', 'account']);

  const user = useAppSelector(userSelector);
  const {
    /**
     * is_kyc_done is true when SingleFlowCountryUser is verified (OR) MultiFlowCountryUser is level three verified.
     */
    is_kyc_done: isKycDone,
    is_kyc_provisioned: isKycProvisioned,
    is_kyc_refresh_required: isKycRefreshRequired,
    country,
  } = user;
  /**
   * Other than is_kyc_done = true, User is also considered verified when
   * 1. He is marked for kyc refresh (is_kyc_refresh_required = true)
   *            (OR)
   * 2. He is marked as provisional kyc (is_kyc_provisioned = true)
   */
  const isUserVerified = isKycDone || isKycRefreshRequired || isKycProvisioned;

  const isSingleFlowCountry = includes(country, COUNTRIES_WITH_SINGLE_VERIFICATION_FLOW);

  const levelOneVerified = useAppSelector(isUserLevelOneVerifiedSelector);
  const levelTwoVerified = useAppSelector(isUserLevelTwoVerifiedSelector);
  const levelThreeVerified = useAppSelector(isUserLevelThreeVerifiedSelector);

  const onlyLevel2Verified = useAppSelector(onlyLevel2VerifiedSelector);

  const { navigateToVerification } = useRekycVerificationNavigation('My Account Tag');
  const onKycStatusClick = () => {
    navigateToVerification();
  };

  return (
    (<DeltaButton
      useBasicButton
      data-testid="kycStatusIndicator-profileMenu"
      onClick={onKycStatusClick}
      className={cn(styles.wrapperButton, { buttonClassName })}
    >
      <Render when={!isKycRefreshRequired && (levelThreeVerified || levelTwoVerified)}>
        <div className={styles.verified}>
          <TickVerified />
          <span>
            {t(
              `account:profile.kycLevelNames.${
                IS_INDIAN_EXCHANGE || onlyLevel2Verified ? 'verified' : 'verifiedPlus'
              }`
            )}
          </span>
        </div>
      </Render>
      <Render when={!isKycDone && isKycProvisioned}>
        <div className={styles.provisional}>
          <TickYellow />
          <span className={styles.text}>
            {t(`headerFooter:header.profile.provisional`)}
          </span>
        </div>
      </Render>
      <Render when={isKycRefreshRequired}>
        <div className={cn(styles.refreshRequired, styles.notVerified)}>
          <RedWarningIcon />
          <span className={styles.text}>{textKycRefreshRequired}</span>
        </div>
      </Render>
      <Render when={!isUserVerified && isSingleFlowCountry}>
        <div className={styles.notVerified}>
          <UnverifiedIcon />
          <span className={styles.text}>
            {t(`headerFooter:header.profile.getVerified`)}
          </span>
        </div>
      </Render>
      <Render when={!isUserVerified && !isSingleFlowCountry}>
        {/* Fully Unverified User */}
        <Render when={!levelOneVerified && !levelTwoVerified}>
          <div className={styles.notVerified}>
            <UnverifiedIcon />
            <span className={styles.text}>
              {t(`headerFooter:header.profile.getVerified`)}
            </span>
          </div>
        </Render>

        {/* Just Phone Verified a.k.a Level One */}
        <Render when={levelOneVerified && !levelTwoVerified}>
          <Render
            when={isUserPhoneVerifiedBeforeLevelOneInvalidation(user)}
            else={
              <div className={styles.notVerified} data-palette="KycStatusButton">
                <UnverifiedIcon />
                <span className={styles.text}>
                  {t(`headerFooter:header.profile.getVerified`)}
                </span>
              </div>
            }
          >
            <RenderForGlobalExchange>
              <div className={cn(styles.refreshRequired, styles.notVerified)}>
                <RedWarningIcon />
                <span className={styles.text}>
                  {t(`headerFooter:header.profile.kycUpgradeRequired`)}
                </span>
              </div>
            </RenderForGlobalExchange>
          </Render>
        </Render>

        {/* Level Two Verified */}
        <Render when={onlyLevel2Verified && !isKycRefreshRequired}>
          <div className={styles.verified}>
            <TickVerified />
            {t('account:profile.kycLevelNames.verified')}
          </div>
        </Render>
      </Render>
    </DeltaButton>)
  );
};

export default KycStatusButton;
